import './index.scss';
import dayjs from 'dayjs';
import Header from '../../../../../../components/Header/Header';
import QuickActions from '../../../../../../components/QuickActions';
import NoData from '../../../../../../components/NoData/NoData';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { copyToClipboard, formatToNaira, useDebounce } from '../../../../../../helpers/utils';
import { IoCopyOutline } from 'react-icons/io5';
import { Loader } from '@piggytechng/component-library';
import { HiDotsVertical } from 'react-icons/hi';
import GetItPurchaseDetails from '../../../../../../components/NewSideSheet/GetItPurchaseDetails';
import useGetIt from '../../../../../../hooks/useGetIt';
import failureIcon from '../../../../../../assets/icons/failureIcon.svg';
import successIcon from '../../../../../../assets/icons/succesIcon.svg';

const SingleGetIt = () => {
  const params = useParams();
  const userId = params.id as string;
  const purchaseId = params.purchaseId as string;
  const activeTab = params.activeTab as string;
  const [loading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { subscriptionDetails, fetchingDetails } = useGetIt({ subId: purchaseId });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const transactionsWithBalance = useMemo(() => {
    if (!subscriptionDetails?.transactions) return [];

    let current = Number(subscriptionDetails.amounts.total_saved);
    return subscriptionDetails.transactions.map((tx: any) => {
      const balanceAfterThis = current;
      if (tx.type === 'credit') {
        current -= Number(tx.amount);
      } else {
        current += Number(tx.amount);
      }
      return { ...tx, calculated_balance: balanceAfterThis };
    });
  }, [subscriptionDetails]);

  const filteredTransactions = useMemo(() => {
    if (!transactionsWithBalance) return [];
    return transactionsWithBalance.filter(
      (item: any) =>
        item?.external_reference?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        item?.transaction_id?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        item?.amount?.toString().includes(debouncedSearchTerm),
    );
  }, [transactionsWithBalance, debouncedSearchTerm]);

  if (!subscriptionDetails && fetchingDetails) {
    return (
      <div
        className="page-loader"
        style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Loader />
      </div>
    );
  }

  if (!subscriptionDetails) {
    return <NoData message="Subscription details not found." />;
  }

  const handleRowClick = (purchase: any) => {
    setSelectedPurchase(purchase);
    setShowDetails(true);
  };

  return (
    <div className="single-target-page single-getit-page">
      <Header name="Purchase Details" backButton />

      {loading ? (
        <div className="page-loader">
          <Loader />
        </div>
      ) : (
        <div className="content">
          <div className="target-header">
            <div className="left-section">
              <h1>
                {subscriptionDetails.purchase.item.title}.{' '}
                {subscriptionDetails.status && (
                  <span className="status-badge active" style={{ backgroundColor: '#FFF2EA', color: '#F37932' }}>
                    {subscriptionDetails.status}
                  </span>
                )}
              </h1>
              <span className="flex--2 cont">
                <p>Purchase ID: {subscriptionDetails.reference} </p>
                <IoCopyOutline
                  size={18}
                  className="pointer"
                  onClick={() => copyToClipboard('Purchase ID', subscriptionDetails.reference)}
                />{' '}
              </span>
            </div>
            <div className="right-section">
              <QuickActions
                activeTabWallet="getit"
                userId={userId}
                targetId={purchaseId}
                isWalletTab={false}
                activeGetItTab={activeTab}
              />
            </div>
          </div>

          <div className="target-details">
            <div className="detail">
              <p>Intended Amount</p>
              <h1>{formatToNaira(subscriptionDetails.amounts.target_amount)}</h1>
            </div>
            <div className="detail">
              <p>Balance</p>
              <h1>{formatToNaira(subscriptionDetails.amounts.total_saved)}</h1>
            </div>
            <div className="detail">
              <p>Frequency</p>
              <h1>
                {formatToNaira(subscriptionDetails.amounts.per_debit_amount)}{' '}
                {subscriptionDetails.payment_schedule.cadence_label}
              </h1>
            </div>
            <div className="detail">
              <p>Start Date</p>
              <h1>{dayjs(subscriptionDetails.duration.created_at).format('D MMM, YYYY')}</h1>
            </div>
            <div className="detail">
              <p>End Date</p>
              <h1>{dayjs(subscriptionDetails.duration.target_date).format('D MMM, YYYY')}</h1>
            </div>
            <div className="detail">
              <p>Duration</p>
              <h1>{subscriptionDetails.duration.days_remaining} days left</h1>
            </div>
          </div>

          <h1 className="target-history-heading">Purchase History</h1>

          <div className="customers-filter">
            <div className="input-field">
              <form action="" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="text"
                  placeholder="Search by Transaction ID, External Ref, amount"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ fontSize: '14px' }}
                />
              </form>
            </div>
            <div className="query-filter">
              <div>
                <svg width="10" height="10" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0.75 0.75H17.25M6.75 13.25H11.25M3.75 7H14.25"
                    stroke="#1E293B"
                    strokeWidth="1.5"
                    strokeLinecap="square"
                  />
                </svg>
              </div>
              <div>Search</div>
            </div>
            <div className="query-filter pointer" onClick={() => setSearchTerm('')}>
              <div></div>
              <div>Reset</div>
            </div>
          </div>

          {filteredTransactions && filteredTransactions.length ? (
            <div className="table-container housemoney">
              <table className="table">
                <thead className="table_head">
                  <tr>
                    <th className="icon">{''}</th>
                    <th className="userId">Transaction ID</th>
                    <th className="userId">External Ref</th>
                    {/* <th className="email">Email</th> */}
                    <th className="amount">Amount</th>
                    <th className="product">Balance</th>
                    <th className="duration">Date</th>
                    <th className="action">{''}</th>
                  </tr>
                </thead>

                <tbody className="table-body">
                  {filteredTransactions.map((item, index) => (
                    <tr key={index} className="table-row pointer" onClick={() => handleRowClick(item)}>
                      <td className="icon">
                        <img src={item.type === 'credit' ? successIcon : failureIcon} alt="icon" />
                      </td>
                      <td className="userId">
                        <div
                          className="flex--2 items-center"
                          style={{ gap: '8px' }}
                          onClick={(e) => {
                            if (!item.transaction_id) return;
                            e.stopPropagation();
                            copyToClipboard('Transaction ID', item.transaction_id);
                          }}
                        >
                          <span
                            style={{
                              textAlign: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {item.transaction_id ? item.transaction_id : 'N/A'}
                          </span>
                          {item.transaction_id && <IoCopyOutline size={14} className="pointer" />}
                        </div>
                      </td>

                      <td className="userId">
                        <div
                          className="flex--2 items-center"
                          style={{ gap: '8px' }}
                          onClick={(e) => {
                            if (!item.external_reference) return;
                            e.stopPropagation();
                            copyToClipboard('Transaction ID', item.external_reference);
                          }}
                        >
                          <span
                            style={{
                              textAlign: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {item.external_reference ? item.external_reference : 'N/A'}
                          </span>
                          {item.external_reference && <IoCopyOutline size={14} className="pointer" />}
                        </div>
                      </td>
                      {/* <td className="email">{singleCustomer?.email || subscriptionDetails.reference}</td> */}
                      <td className="amount">{formatToNaira(Number(item.amount))}</td>
                      <td className="product">{formatToNaira(item.calculated_balance)}</td>
                      <td className="duration">{dayjs(item?.created_at).format('D MMM, YYYY')}</td>
                      <td className="action">
                        <HiDotsVertical size={18} color="#94A3B8" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData message={searchTerm ? 'No results found for your search.' : 'No transaction history available.'} />
          )}
        </div>
      )}

      <GetItPurchaseDetails
        show={showDetails}
        onClose={() => setShowDetails(false)}
        data={selectedPurchase}
        subscription={subscriptionDetails}
        subId={purchaseId}
      />
    </div>
  );
};

export default SingleGetIt;
